<template>
  <div class="text-xs">
    <LoadingIndicator v-if="loading" message="Loading contracts..." />

    <div v-else-if="error" class="text-red-600">
      Couldn't load contracts.
      <button type="button" class="underline" @click="fetchGrid">Retry</button>
    </div>

    <div v-else-if="!rows.length" class="text-gray-500">
      No contracts found for this tenant.
    </div>

    <!-- GRID. Kept mounted behind v-show, not v-if: returning from a roster is
         then instant and needs no refetch. -->
    <template v-else>
      <div v-show="!roster.open" class="border border-gray-200 rounded overflow-auto max-h-[620px]">
        <table class="w-full">
          <thead class="sticky top-0 bg-white shadow-sm">
            <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
              <th class="py-2 px-3">Payer</th>
              <th class="py-2 px-3">LOB</th>
              <th class="py-2 px-3">Contract</th>
              <th v-for="y in years" :key="y" class="py-2 px-3 text-right">{{ y }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in rows" :key="r.CONTRACT_ID">
              <!-- Clicking anywhere on the row EXCEPT a count toggles lineage;
                   the counts keep their own click (the roster). -->
              <tr
                class="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                :class="expanded === r.CONTRACT_ID ? 'bg-gray-50' : ''"
                @click="toggleLineage(r)"
            >
                <td class="py-1.5 px-3 text-gray-700">
                  <font-awesome-icon
                    :icon="['fas', expanded === r.CONTRACT_ID ? 'chevron-down' : 'chevron-right']"
                    class="text-gray-400 mr-2"
                  />
                  {{ r.PAYER_ID || '—' }}
                </td>
                <td class="py-1.5 px-3 text-gray-500">{{ r.LOB || '—' }}</td>
                <td class="py-1.5 px-3">{{ r.CONTRACT_ID }}</td>
                <td
                  v-for="y in years"
                  :key="y"
                  class="py-1.5 px-3 text-right"
                  :class="r.cells[y] ? 'font-medium' : 'text-gray-300'"
              >
                  <button
                    v-if="r.cells[y]"
                    type="button"
                    class="text-blue-600 hover:underline"
                    :title="cellTitle(r.cells[y])"
                    @click.stop="openRoster(r, y)"
                >{{ fmt(r.cells[y].PATIENTS) }}</button>
                  <span v-else>—</span>
                </td>
              </tr>

              <!-- LINEAGE: the deliveries that resolved into this contract -->
              <tr v-if="expanded === r.CONTRACT_ID" class="bg-gray-50 border-b border-gray-200">
                <td :colspan="years.length + 3" class="px-3 py-3">
                  <LoadingIndicator v-if="lineage.loading" message="Loading lineage..." />
                  <div v-else-if="!lineage.rows.length" class="text-gray-500">
                    No deliveries recorded.
                  </div>
                  <template v-else>
                    <div class="text-gray-500 mb-2">
                      {{ lineage.rows.length }} deliveries ·
                      {{ lineageSources.length }} feed{{ lineageSources.length === 1 ? '' : 's' }} ·
                      {{ lineageLabels.length }} label{{ lineageLabels.length === 1 ? '' : 's' }}
                    </div>
                    <table class="w-full bg-white border border-gray-200 rounded">
                      <thead>
                        <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
                          <th class="py-1.5 px-2">Feed</th>
                          <th class="py-1.5 px-2">Label</th>
                          <th class="py-1.5 px-2">PY</th>
                          <th class="py-1.5 px-2">Report date</th>
                          <th class="py-1.5 px-2">Period</th>
                          <th class="py-1.5 px-2 text-right">Patients</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(d, i) in lineage.rows" :key="i" class="border-b border-gray-50">
                          <td class="py-1 px-2">{{ d.SOURCE }}</td>
                          <td class="py-1 px-2">
                            {{ d.CONTRACT_ID }}
                            <span
                              v-if="d.CONTRACT_ID !== d.CANONICAL_CONTRACT_ID"
                              class="ml-1 text-gray-500 bg-gray-100 rounded px-1"
                              title="Unified into this contract by the engine"
                            >alias</span>
                          </td>
                          <td class="py-1 px-2">{{ d.PERFORMANCE_YEAR }}</td>
                          <td class="py-1 px-2">{{ d.REPORT_DATE || '—' }}</td>
                          <td class="py-1 px-2 text-gray-500">
                            {{ d.VALID_FROM }} → {{ d.VALID_THRU }}
                          </td>
                          <td class="py-1 px-2 text-right">{{ fmt(d.N_PATIENTS) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ROSTER -->
      <div v-if="roster.open">
        <div class="flex items-center gap-3 mb-3">
          <button
            type="button"
            class="text-blue-600 hover:underline"
            @click="closeRoster"
        >
            <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-1" />
            Contracts
          </button>
          <span class="text-gray-300">|</span>
          <span class="font-medium text-gray-900">
            {{ roster.payer || '—' }} · {{ roster.contractId }} · {{ roster.py }}
          </span>
          <span class="text-gray-500">
            {{ fmt(roster.total) }} patients{{ roster.truncated ? ` (showing first ${fmt(roster.rows.length)})` : '' }}
          </span>
        </div>

        <LoadingIndicator v-if="roster.loading" message="Loading roster..." />
        <div v-else-if="roster.error" class="text-sm text-gray-600 py-6 text-center">
          Couldn't load the roster.
          <button class="text-primary-500 hover:underline ml-1" @click="closeRoster">Back to grid</button>
        </div>
        <div v-else class="border border-gray-200 rounded overflow-auto max-h-[620px]">
          <table class="w-full">
            <thead class="sticky top-0 bg-white shadow-sm">
              <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
                <th class="py-2 px-3">Patient</th>
                <th class="py-2 px-3">DOB</th>
                <th class="py-2 px-3 text-right">Age</th>
                <th class="py-2 px-3">Location</th>
                <th class="py-2 px-3">Assigned NPI</th>
                <th class="py-2 px-3">TIN</th>
                <th class="py-2 px-3">CCN</th>
                <th class="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="p in roster.rows" :key="p.PATIENT_MASTER">
                <tr
                  class="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                  :class="expandedPatient === p.PATIENT_MASTER ? 'bg-gray-50' : ''"
                  @click="togglePatient(p)"
              >
                  <td class="py-1.5 px-3">
                    <font-awesome-icon
                      :icon="['fas', expandedPatient === p.PATIENT_MASTER ? 'chevron-down' : 'chevron-right']"
                      class="text-gray-400 mr-2"
                    />
                    <span v-if="p.LAST_NAME" class="sensitive">{{ p.LAST_NAME }}, {{ p.FIRST_NAME }}</span>
                    <span v-else class="text-gray-400 italic">no name on file</span>
                    <span v-if="p.DEATH_DATE" class="ml-2 text-red-600">deceased</span>
                  </td>
                  <td class="py-1.5 px-3 sensitive">{{ p.DOB || '—' }}</td>
                  <td class="py-1.5 px-3 text-right">{{ p.AGE ?? '—' }}</td>
                  <td class="py-1.5 px-3 text-gray-500">
                    {{ [p.STATE, p.ZIP].filter(Boolean).join(' ') || '—' }}
                  </td>
                  <td class="py-1.5 px-3 sensitive">{{ p.PRIMARY_NPI || '—' }}</td>
                  <td class="py-1.5 px-3 sensitive">{{ p.PRIMARY_TIN || '—' }}</td>
                  <td class="py-1.5 px-3">{{ p.PRIMARY_CCN || '—' }}</td>
                  <!-- The inline expander shows this patient's evidence; the
                       full record adds their other contracts and identifier
                       counts, which the roster cannot show. -->
                  <td class="py-1.5 px-3 text-right">
                    <button
                      type="button"
                      class="text-blue-600 hover:underline"
                      @click.stop="$emit('open-patient', p.PATIENT_MASTER)"
                    >Open &rarr;</button>
                  </td>
                </tr>

                <!-- EVIDENCE: one row per feed per value. -->
                <tr
                  v-if="expandedPatient === p.PATIENT_MASTER"
                  class="bg-gray-50 border-b border-gray-200"
              >
                  <td colspan="8" class="px-3 py-3">
                    <LoadingIndicator v-if="evidence.loading" message="Loading evidence..." />
                    <div v-else-if="!evidence.rows.length" class="text-gray-500">
                      No evidence recorded.
                    </div>
                    <div v-else class="border border-gray-200 rounded overflow-auto max-h-[420px]">
                      <table class="w-full bg-white">
                        <thead class="sticky top-0 bg-white shadow-sm">
                          <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
                            <th class="py-2 px-3">Domain</th>
                            <th class="py-2 px-3">Feature</th>
                            <th class="py-2 px-3">Value</th>
                            <th class="py-2 px-3">Source</th>
                            <th class="py-2 px-3 text-right">Rank</th>
                            <th class="py-2 px-3 text-right">Records</th>
                            <th class="py-2 px-3">Period</th>
                            <th class="py-2 px-3">Last report</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(e, i) in evidence.rows"
                            :key="i"
                            class="border-b border-gray-50"
                            :class="truthy(e.IS_WINNER) ? 'bg-green-50' : ''"
                        >
                            <td class="py-1.5 px-3 text-gray-500">{{ e.DOMAIN }}</td>
                            <td class="py-1.5 px-3 text-gray-500">{{ e.FEATURE }}</td>
                            <td class="py-1.5 px-3" :class="truthy(e.IS_WINNER) ? 'font-medium' : ''">
                              {{ e.VALUE }}
                            </td>
                            <td class="py-1.5 px-3">
                              {{ e.SOURCE || '—' }}
                              <span v-if="truthy(e.IS_PRIMARY_SOURCE)" class="text-blue-700">
                                (primary)
                              </span>
                            </td>
                            <td class="py-1.5 px-3 text-right text-gray-500">
                              {{ e.SOURCE_RELIABILITY ?? '—' }}
                            </td>
                            <td class="py-1.5 px-3 text-right">{{ fmt(e.RECORD_COUNT) }}</td>
                            <td class="py-1.5 px-3 text-gray-500">
                              {{ e.VALID_FROM ? `${e.VALID_FROM} → ${e.VALID_THRU || 'open'}` : '—' }}
                            </td>
                            <td class="py-1.5 px-3 text-gray-500">
                              {{ e.LAST_REPORT_DATE || '—' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import {
  getContractGrid, listContractPatients, getContractLineage,
  getPatientEvidence,
} from '@/services/api/mdr/read';

const ROSTER_LIMIT = 500;

const emptyRoster = () => ({
  open: false,
  cohort: null,
  contractId: '',
  payer: '',
  py: null,
  total: 0,
  rows: [],
  loading: false,
  truncated: false,
  error: false,
});

export default {
  name: 'MDRContracts',
  components: { LoadingIndicator },
  emits: ['open-patient'],
  data() {
    return {
      loading: false,
      error: false,
      years: [],
      rows: [],
      roster: emptyRoster(),
      expanded: null,
      lineage: { rows: [], loading: false },
      expandedPatient: null,
      evidence: { rows: [], loading: false },
    };
  },
  computed: {
    lineageSources() {
      return [...new Set(this.lineage.rows.map((d) => d.SOURCE))];
    },
    lineageLabels() {
      return [...new Set(this.lineage.rows.map((d) => d.CONTRACT_ID))];
    },
  },
  async mounted() {
    await this.fetchGrid();
  },
  methods: {
    async fetchGrid() {
      this.loading = true;
      this.error = false;
      this.roster = emptyRoster();
      this.expanded = null;
      try {
        const d = await getContractGrid();
        this.years = d.years || [];
        this.rows = this.pivot(d.cells || []);
      } catch (e) {
        this.years = [];
        this.rows = [];
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    // Flat (contract, year) cells -> one row per contract keyed by year.
    pivot(cells) {
      const byContract = new Map();
      cells.forEach((c) => {
        let row = byContract.get(c.CONTRACT_ID);
        if (!row) {
          row = {
            CONTRACT_ID: c.CONTRACT_ID,
            PAYER_ID: c.PAYER_ID,
            LOB: c.LOB,
            cells: {},
          };
          byContract.set(c.CONTRACT_ID, row);
        }
        row.cells[c.PERFORMANCE_YEAR] = c;
      });
      return [...byContract.values()].sort((a, b) => {
        const p = (a.PAYER_ID || '').localeCompare(b.PAYER_ID || '');
        return p || a.CONTRACT_ID.localeCompare(b.CONTRACT_ID);
      });
    },
    async toggleLineage(row) {
      if (this.expanded === row.CONTRACT_ID) {
        this.expanded = null;
        return;
      }
      this.expanded = row.CONTRACT_ID;
      this.lineage = { rows: [], loading: true };
      try {
        this.lineage.rows = await getContractLineage(row.CONTRACT_ID);
      } catch (e) {
        this.lineage.rows = [];
      } finally {
        this.lineage.loading = false;
      }
    },
    async openRoster(row, year) {
      const cell = row.cells[year];
      this.roster = {
        ...emptyRoster(),
        open: true,
        cohort: cell.COHORT_MASTER,
        contractId: row.CONTRACT_ID,
        payer: row.PAYER_ID,
        py: year,
        total: cell.PATIENTS,
        loading: true,
      };
      this.expandedPatient = null;
      try {
        const rows = await listContractPatients(cell.COHORT_MASTER, year, ROSTER_LIMIT);
        this.roster.rows = rows;
        this.roster.truncated = rows.length >= ROSTER_LIMIT;
      } catch (e) {
        // Was try/finally with no catch — a failed roster fetch left the view
        // open and empty with an unhandled rejection, indistinguishable from
        // "no patients".
        this.roster.error = true;
      } finally {
        this.roster.loading = false;
      }
    },
    closeRoster() {
      // Grid is still mounted (v-show), so this returns with no refetch.
      this.roster = emptyRoster();
      this.expandedPatient = null;
    },
    async togglePatient(p) {
      if (this.expandedPatient === p.PATIENT_MASTER) {
        this.expandedPatient = null;
        return;
      }
      this.expandedPatient = p.PATIENT_MASTER;
      this.evidence = { rows: [], loading: true };
      try {
        this.evidence.rows = await getPatientEvidence(p.PATIENT_MASTER);
      } catch (e) {
        this.evidence.rows = [];
      } finally {
        this.evidence.loading = false;
      }
    },
    truthy(v) {
      return v === true || v === 1 || v === '1' || v === 'true';
    },
    fmt(n) {
      return n == null ? '—' : Number(n).toLocaleString();
    },
    // The cell shows CURRENT membership (the contract's latest delivery). The
    // union of every delivery that year is usually much larger — surfaced on
    // hover so the difference is discoverable without cluttering the grid.
    cellTitle(c) {
      const cur = this.fmt(c.PATIENTS);
      const asOf = c.LATEST_REPORT_DATE ? ` as of ${c.LATEST_REPORT_DATE}` : '';
      return c.PATIENTS_EVER > c.PATIENTS
        ? `${cur} in the latest delivery${asOf}; `
          + `${this.fmt(c.PATIENTS_EVER)} attributed at some point this year`
        : `${cur}${asOf}`;
    },
  },
};
</script>
